// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Generic drag-drop upload area. No slots, no grouping.
 * Used by Contract Review and Public Portal (simple mode).
 */
import { useCallback, useState, type FC, type DragEvent } from 'react'
import { Upload, Loader2, AlertCircle, CheckCircle2, FileText } from 'lucide-react'
import { Alert, AlertDescription, Progress, cn } from '@/components/ui'
import type { UploadingFile } from '../hooks'

interface DocumentUploadZoneProps {
  onUpload: (files: File[]) => Promise<void>
  accept?: string
  multiple?: boolean
  disabled?: boolean
  /** In-progress uploads to display */
  uploading?: UploadingFile[]
}

export const DocumentUploadZone: FC<DocumentUploadZoneProps> = ({
  onUpload,
  accept = '.pdf,.doc,.docx,.txt',
  multiple = true,
  disabled = false,
  uploading = [],
}) => {
  const [dragActive, setDragActive] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleUpload = useCallback(async (incoming: FileList | null) => {
    if (!incoming?.length) return
    const files = Array.from(incoming)

    setMessage(null)
    setError(null)

    try {
      await onUpload(files)
      setMessage(`Uploaded ${files.length === 1 ? files[0].name : `${files.length} files`}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
    }
  }, [onUpload])

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setDragActive(false)
    void handleUpload(event.dataTransfer.files)
  }

  return (
    <div className="space-y-3">
      {message && (
        <Alert variant="default" className="border border-emerald-200 bg-emerald-50">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          <AlertDescription className="text-emerald-800">{message}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div
        className={cn(
          'relative rounded-xl border border-dashed transition-all duration-200',
          dragActive
            ? 'border-blue-500 bg-blue-50'
            : 'border-slate-300 bg-slate-50/40 hover:border-slate-400 hover:bg-slate-50',
          disabled && 'pointer-events-none opacity-50',
        )}
        onDragEnter={e => { e.preventDefault(); e.stopPropagation(); setDragActive(true) }}
        onDragOver={e => { e.preventDefault(); e.stopPropagation() }}
        onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setDragActive(false) }}
        onDrop={handleDrop}
      >
        <div className="flex min-h-20 items-center justify-center gap-3 px-6 py-5">
          <div className={cn(
            'flex size-9 items-center justify-center rounded-lg border bg-white shadow-sm transition-transform duration-200',
            dragActive && 'scale-105 border-blue-300',
          )}>
            <Upload className={cn('h-4 w-4', dragActive ? 'text-blue-600' : 'text-slate-600')} />
          </div>
          <div className="text-sm text-slate-600">
            <label className="cursor-pointer font-semibold text-slate-900 hover:text-blue-700">
              Click to upload
              <input
                type="file"
                multiple={multiple}
                className="hidden"
                accept={accept}
                onChange={e => void handleUpload(e.target.files)}
                disabled={disabled}
              />
            </label>
            <span> or drag files here</span>
          </div>
          <span className="text-xs text-slate-400">PDF, DOCX, TXT · up to 25 MB</span>
        </div>
      </div>

      {uploading.length > 0 && (
        <div className="space-y-2">
          {uploading.map(file => (
            <div key={file.id} className="flex items-center gap-3 rounded border bg-white px-3 py-2">
              <FileText className="h-4 w-4 text-slate-500" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{file.name}</p>
              </div>
              {file.error ? (
                <span className="text-xs text-red-500">{file.error}</span>
              ) : file.completed ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              ) : (
                <div className="flex items-center gap-2">
                  <Progress value={file.progress} className="w-20 h-1.5" />
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
